import { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useSelector } from 'react-redux'
import { Navigate, useLocation } from 'react-router-dom'

import { useLocales } from 'locales/hooks'
import { getApplicationSystemInfo, tagManager } from 'shared/utils'
import { SitemodelContext, useSitemodelContent } from 'sitemodel/shared/hooks'
import { ApplicationSystem } from 'sitemodel/shared/hooks/useSitemodelLocalParams/useSitemodelLocalParams'
import { Renderer } from './Renderer'
import { isInaccessibleSitemodel } from './Load.utils'
import useHomeSitemodels from './shared/hooks/useHomeSitemodels'
import useLogoutOnInaccessibleHome from './shared/hooks/useLogoutOnInaccessibleHome'
import useSitemodelFromUrl from './shared/hooks/useSitemodelFromUrl'
import { AuthenticationType } from 'authentication/shared/types'
import { YesObject } from '@yes.technology/react-toolkit'

type LoadProps = {
  isIndex?: boolean
  preloadedSitemodelData?: YesObject | false
}

const Load = ({ isIndex, preloadedSitemodelData }: LoadProps) => {
  const [sitemodelContentFromParent, setSitemodelContentFromParent] =
    useState(null)

  useEffect(() => {
    const onReceivedMessage = (event: MessageEvent) => {
      if (event.data?.type === 'SITEMODEL_CONTENT') {
        const { payload } = event.data
        setSitemodelContentFromParent(payload)
      }
    }

    globalThis.addEventListener('message', onReceivedMessage)
    return () => globalThis.removeEventListener('message', onReceivedMessage)
  }, [])

  const { sitemodel, isFetched } = useSitemodelFromUrl({
    enabled: !sitemodelContentFromParent && !preloadedSitemodelData
  })

  // if pathname is '/' fetch home sitemodels
  const { homeSitemodels, isFetched: isFetchedHome } = useHomeSitemodels()

  useLogoutOnInaccessibleHome({ homeSitemodels, isFetchedHome })

  const location = useLocation()

  const { getSitemodelByLocale } = useLocales()
  const accessRestrictionSitemodelUuid = getSitemodelByLocale({
    demand: 'accessRestrictionSitemodelUuid'
  })

  const unauthorizedSitemodelUuid = getSitemodelByLocale({
    demand: 'unauthorizedSitemodelUuid'
  })

  const currentSitemodel = isIndex
    ? homeSitemodels?.[0]
    : preloadedSitemodelData
      ? preloadedSitemodelData
      : sitemodel

  const rawContent =
    sitemodelContentFromParent ?? currentSitemodel?.sitemodel_content

  const content = useSitemodelContent({
    sitemodelContent: rawContent
  })

  const documentTitle = content?.title || currentSitemodel?.des

  const { token, tokenType, username } = useSelector(
    ({ authentication }: AuthenticationType) => authentication
  )

  const isUserLogged = token && tokenType === 'private'

  useEffect(() => {
    tagManager.setDataLayer({
      event: 'pageview',
      page: {
        url: location.pathname,
        title: documentTitle
      },
      user: {
        username
      }
    })
  }, [documentTitle, location.pathname, username])

  const applicationSystem = useMemo<Partial<ApplicationSystem>>(() => {
    const { ownerUuid } = getApplicationSystemInfo()
    return { owner: ownerUuid || '' }
  }, [])

  const { cloudfrontUrl, globalComponents: globalComponentsObject } = globalThis

  const globalComponents = (globalComponentsObject ?? [])?.map((object) => {
    return object?.component
  })

  if (isInaccessibleSitemodel({ isFetched, isFetchedHome, isIndex, content })) {
    const redirectOrigin = `${location.pathname}${location.search}${location.hash}`
    const redirectOriginURI = `?redirectOrigin=${encodeURIComponent(redirectOrigin)}`
    const redirectURI = `/${isUserLogged ? accessRestrictionSitemodelUuid : unauthorizedSitemodelUuid + redirectOriginURI}`

    return (
      <Navigate
        to={redirectURI}
        replace
        state={{
          from: location
        }}
      />
    )
  }

  return (
    <>
      <Helmet>
        <title>{documentTitle}</title>
        {applicationSystem?.owner && (
          <link
            rel='icon'
            href={`${cloudfrontUrl}/static-files/favicon/${applicationSystem.owner}.ico`}
          />
        )}
        {applicationSystem?.owner && (
          <link
            rel='manifest'
            href={`${cloudfrontUrl}/static-files/pwa/${applicationSystem.owner}/manifest.json`}
            crossOrigin='anonymous'
          />
        )}
      </Helmet>

      <SitemodelContext.Provider value={content}>
        <Renderer
          {...content}
          key={currentSitemodel?.uuid}
          globalComponents={globalComponents}
        />
      </SitemodelContext.Provider>
    </>
  )
}

export default Load
